import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { EditorComponent } from '@tinymce/tinymce-angular';
import {
  POTENTIAL_CLIENT_ACTION_TYPES,
  POTENTIAL_CLIENT_ACTION_TYPES_ALL,
  POTENTIAL_CLIENT_REJECTION_REASONS,
  PotentialClient,
  PotentialClientActionType,
} from 'src/app/models/potential-client.interface';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { RingoverDialerService } from 'src/app/services/ringover-dialer.service';
import { PotentialClientCallService } from 'src/app/services/potential-client-call.service';
import { TinymceService } from 'src/app/services/tinymce.service';
import { AddPotentialClientDialogComponent } from '../add-potential-client-dialog/add-potential-client-dialog.component';
import { PotentialClientChangeStatusDialogComponent } from './potential-client-change-status-dialog/potential-client-change-status-dialog.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCell } from '@angular/material/table';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { DatePipe, NgClass } from '@angular/common';
import { LocationsManagementComponent } from 'src/app/components/main/shared/locations/locations-management/locations-management.component';
import { LocationRelatableTypeEnum } from 'src/app/enums/location-relatable-type.enum';
import { MatTooltip } from '@angular/material/tooltip';
import { TrustedHtmlPipe } from 'src/app/pipes/trusted-html.pipe';

@Component({
  selector: 'app-potential-client-detail',
  templateUrl: './potential-client-detail.component.html',
  styleUrls: ['./potential-client-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButton,
    RouterLink,
    MatIcon,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCell,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    ReactiveFormsModule,
    MatTooltip,
    DatePipe,
    LocationsManagementComponent,
    NgClass,
    EditorComponent,
    TrustedHtmlPipe,
  ],
})
export class PotentialClientDetailComponent implements OnInit {
  protected activatedRoute = inject(ActivatedRoute);
  private api = inject(ApiService);
  protected fb = inject(FormBuilder);
  private dialog = inject(MatDialog);

  readonly potential_client_relatable_type = LocationRelatableTypeEnum.PotentialClient;

  potential_client_id: string = null as any;
  potential_client: PotentialClient = null as any;
  quickActionTypes = POTENTIAL_CLIENT_ACTION_TYPES;
  actionTypes = POTENTIAL_CLIENT_ACTION_TYPES_ALL;
  rejectionReasons = POTENTIAL_CLIENT_REJECTION_REASONS;

  addCommentFormGroup: FormGroup;
  queryParamBackObject: any = {};
  tinyMceApiKey: string;

  tiny_mce_comment_init = {
    automatic_uploads: true,
    statusbar: false,
    menubar: false,
    block_unsupported_drop: false,
    ui_mode: 'split' as const,
    height: 220,
    width: '100%',
    resize: true,
    plugins:
      'anchor autolink charmap codesample directionality help link lists media nonbreaking searchreplace table visualblocks wordcount advlist preview emoticons',
    toolbar:
      'undo redo | blocks | bold italic underline strikethrough | forecolor backcolor | link | align bullist numlist | indent outdent | removeformat',
  };

  private ringoverDialer = inject(RingoverDialerService);
  private potentialClientCall = inject(PotentialClientCallService);
  private employee = inject(EmployeeService);
  private tinymce = inject(TinymceService);

  constructor() {
    this.potential_client_id = this.activatedRoute.snapshot.paramMap.get('id') as string;
    this.tinyMceApiKey = this.tinymce.getKey();

    this.activatedRoute.params.subscribe({
      next: params => {
        this.potential_client_id = params.id;
        this.addCommentFormGroup?.patchValue({ potential_client_id: this.potential_client_id });
        if (this.potential_client_id) this.fetchPotentialClient();
      },
    });

    this.addCommentFormGroup = this.fb.group({
      potential_client_id: [this.potential_client_id, Validators.required],
      content: ['', Validators.compose([Validators.required, Validators.minLength(10)])],
    });
  }

  ngOnInit(): void {
    this.readBackParameter();
    if (this.ringoverDialer.isEnabledForCurrentUser()) {
      this.ringoverDialer.ensureInitialized();
    } else {
      this.ringoverDialer.destroy();
    }
  }

  get canUseRingover(): boolean {
    return this.ringoverDialer.isEnabledForCurrentUser();
  }

  callLead(): void {
    if (!this.potential_client?.phone || !this.canUseRingover) {
      return;
    }

    void this.potentialClientCall.callLeadAndProcess(this.potential_client_id, this.potential_client.phone, () =>
      this.fetchPotentialClient()
    );
  }

  addComment() {
    this.api.createPotentialClientComment(this.addCommentFormGroup.value).subscribe(() => {
      this.fetchPotentialClient();
      this.addCommentFormGroup.patchValue({ content: null });
      this.addCommentFormGroup.markAsUntouched();
    });
  }

  registerAction(type: PotentialClientActionType) {
    const label = this.actionLabel(type);
    if (!confirm(`¿Registrar acción «${label}»?`)) {
      return;
    }

    this.api.createPotentialClientAction(this.potential_client_id, { type }).subscribe(() => {
      this.fetchPotentialClient();
    });
  }

  actionCount(type: PotentialClientActionType): number {
    return this.potential_client?.potential_client_actions?.filter(action => action.type === type).length ?? 0;
  }

  actionLabel(type: string): string {
    return this.actionTypes.find(a => a.value === type)?.label ?? type;
  }

  rejectionLabel(reason: string | null | undefined): string {
    if (!reason) return '-';
    return this.rejectionReasons.find(r => r.value === reason)?.label ?? reason;
  }

  openChangeStatusDialog() {
    const dialogRef = this.dialog.open(PotentialClientChangeStatusDialogComponent, {
      width: '700px',
      data: {
        potential_client: this.potential_client,
      },
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data != null) {
        this.api.updatePotentialClient(this.potential_client_id, data).subscribe(() => {
          this.employee.refreshActionsRequired();
          this.fetchPotentialClient();
        });
      }
    });
  }

  openEditDialog() {
    const dialogRef = this.dialog.open(AddPotentialClientDialogComponent, {
      data: {
        potential_client: this.potential_client,
      },
      width: '700px',
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data != null) {
        this.api.updatePotentialClient(this.potential_client_id, data).subscribe(() => {
          this.employee.refreshActionsRequired();
          this.fetchPotentialClient();
        });
      }
    });
  }

  deleteMeetingRecord(meetingRecordId: any) {
    if (confirm('¿Estás seguro de que quieres eliminar el acta?')) {
      this.api.deleteMeetingRecord(meetingRecordId).subscribe(() => {
        this.fetchPotentialClient();
      });
    }
  }

  private fetchPotentialClient() {
    this.api
      .getPotentialClient(this.potential_client_id, {
        with: 'meeting_records,potential_client_status,potential_client_status_change_records,commercial,origin,potential_client_comments,potential_client_actions',
      })
      .subscribe(data => {
        this.potential_client = data;
      });
  }

  private readBackParameter() {
    const back_string_param = this.activatedRoute.snapshot.queryParamMap.get('b');
    if (back_string_param == null) {
      return;
    }

    try {
      const parsed = JSON.parse(back_string_param);
      // ngx-index-template espera el mismo formato que escribe el serializer
      // (string CSV), no un array: Angular lo serializa como params repetidos
      // y el cloneObj antiguo los corrompía.
      if (Array.isArray(parsed?.potential_client_status_slug)) {
        parsed.potential_client_status_slug = parsed.potential_client_status_slug.filter(Boolean).join(',');
      }
      this.queryParamBackObject = parsed;
    } catch {
      this.queryParamBackObject = {};
    }
  }
}
